/**
 * WALL constraint — parametric wall attachment (Revit-style). The placement
 * persists edge id + along-edge offset + face side + inset, NOT a world
 * transform, so the object follows wall edits and dragging slides it along
 * the wall (clamped to the edge span). The frame's back rests against the
 * chosen wall face.
 *
 * Two placement flavors, keyed on the type's anchor kind (see SketchObjectTool):
 *  - POINT anchor (toilet, sink): single click, the fixed-size object centers
 *    at the cursor — `resolveHover`.
 *  - LINE anchor (cabinet runs, counters): the user DRAWS a span along a wall
 *    (two clicks); the run's width is the drawn length and the parametric
 *    glyph skins it — `resolveWallSpan`.
 */

import * as THREE from 'three';
import {
  getSketchObjectWallEdgeIdProperty,
  getSketchObjectWallOffsetProperty,
  getSketchObjectWallSideProperty,
  getSketchObjectWallInsetProperty,
  getSketchObjectWidthProperty,
  getSketchObjectDepthProperty,
} from '@pointorama/potree-shared';
import { projectMouseToFloor, snapToWallEdge, SKETCH_OBJECT_WALL_SNAP, type WallEdgeHit } from '../../personas/tools/implementations/WallSketchSnapEngine';
import { closestPointOnEdge, edgeLength as arcEdgeLength, pointAtArcLength } from '../../utils/skeleton/arcGeometry';
import type { Pt2 } from '../../utils/FloorMesh3DBuilder';
import type { PointoramaWallSketch } from '../../utils/PointoramaWallSketch';
import type { PointoramaSketchObject } from '../../utils/PointoramaSketchObject';
import type { RegisteredSketchObjectType } from '../registry';
import { buildFrameFromWall, buildFrameFromWallFaceLine, SKETCH_OBJECT_Z_LIFT } from '../glyphs/frame';
import type { ConstraintStrategy, PlacementContext, ResolvedPlacement } from './types';

/** Resolved centerline geometry of one wall edge. */
export interface WallEdgeGeometry {
  edgeId: string;
  a: { x: number; y: number };
  b: { x: number; y: number };
  /** Along-edge length — arc length when the edge is curved. */
  len: number;
  leftSize: number;
  rightSize: number;
  /** Arc curvature (0 = straight). */
  bulge: number;
}

export function wallEdgeGeometry(skeleton: PointoramaWallSketch, edgeId: string): WallEdgeGeometry | null {
  const edge = skeleton.getEdge(edgeId);
  if (!edge) return null;
  const a = skeleton.getNode(edge.startNodeId);
  const b = skeleton.getNode(edge.endNodeId);
  if (!a || !b) return null;
  if (Math.hypot(b.position.x - a.position.x, b.position.y - a.position.y) < 1e-6) return null;
  const aXY = { x: a.position.x, y: a.position.y };
  const bXY = { x: b.position.x, y: b.position.y };
  return {
    edgeId: edge.id,
    a: aXY,
    b: bXY,
    len: arcEdgeLength(aXY, bXY, edge.bulge),
    leftSize: edge.leftSize ?? 0.1,
    rightSize: edge.rightSize ?? 0.1,
    bulge: edge.bulge ?? 0,
  };
}

/** Angular tolerance for a neighbouring edge to count as a COLINEAR
 *  continuation of a wall line (~5° — detected walls are rarely exact). */
const COLINEAR_DOT = 0.9962;
/** Face-size continuity tolerance across a junction node (m). Generous:
 *  detected wall thicknesses vary by centimeters between edges of the same
 *  physical wall, and a small face step reads better than a hard stop. */
const FACE_MATCH_TOL = 0.05;
/** Colinear-chain hop cap (safety on degenerate skeletons). */
const MAX_CHAIN_HOPS = 8;

/** How far one end of an edge's usable span reaches past its node. */
export interface SpanReach {
  /** Length the wall continues COLINEARLY past this end's node (m). */
  ext: number;
  /** Reach of transverse walls' thickness INTO the span at the final
   *  boundary node, on the object's side (m) — 0 in side-agnostic mode. */
  pad: number;
}

/** An edge's unit chord direction + face sizes (straight chains only). */
function edgeUnit(skeleton: PointoramaWallSketch, edgeId: string): { ux: number; uy: number; left: number; right: number } | null {
  const edge = skeleton.getEdge(edgeId);
  if (!edge) return null;
  const a = skeleton.getNode(edge.startNodeId);
  const b = skeleton.getNode(edge.endNodeId);
  if (!a || !b) return null;
  const dx = b.position.x - a.position.x;
  const dy = b.position.y - a.position.y;
  const len = Math.hypot(dx, dy);
  if (len < 1e-6) return null;
  return { ux: dx / len, uy: dy / len, left: edge.leftSize ?? 0.1, right: edge.rightSize ?? 0.1 };
}

/** The edge's face size toward world direction (nx, ny). */
function faceSizeToward(unit: { ux: number; uy: number; left: number; right: number }, nx: number, ny: number): number {
  return -unit.uy * nx + unit.ux * ny > 0 ? unit.left : unit.right;
}

/**
 * How far the wall line continues past one end of `edgeId` — chains of
 * straight, colinear, same-thickness edges act as ONE wall, so objects can
 * sit/slide across T-junction nodes.
 *
 * With `objectNorm` (unit normal toward the object's side) the walk is
 * side-aware: face continuity is only required on that side, and a transverse
 * wall whose body reaches into the span ON that side blocks the chain — its
 * reach is returned as `pad` so clamps keep the footprint clear of its face.
 * With `objectNorm` null the walk is side-agnostic (both faces must continue,
 * no blocking) — used for draw/resize span bounds.
 */
export function colinearSpanReach(skeleton: PointoramaWallSketch, edgeId: string, end: 'start' | 'end', objectNorm: Pt2 | null): SpanReach {
  const geom = wallEdgeGeometry(skeleton, edgeId);
  const edge = skeleton.getEdge(edgeId);
  // Chains only extend straight lines — arc extrapolation wouldn't follow them.
  if (!geom || !edge || Math.abs(geom.bulge) > 1e-6) return { ext: 0, pad: 0 };

  const startUnit = edgeUnit(skeleton, edgeId);
  if (!startUnit) return { ext: 0, pad: 0 };
  const outSign = end === 'start' ? -1 : 1;
  let dir: Pt2 = { x: startUnit.ux * outSign, y: startUnit.uy * outSign };
  let nodeId = end === 'start' ? edge.startNodeId : edge.endNodeId;
  let currentEdgeId = edgeId;
  let ext = 0;

  for (let hop = 0; hop < MAX_CHAIN_HOPS; hop++) {
    const current = edgeUnit(skeleton, currentEdgeId);
    if (!current) return { ext, pad: 0 };
    let continuation: { id: string; farNodeId: string; len: number; away: Pt2 } | null = null;
    let pad = 0;
    for (const e of skeleton.getEdges()) {
      if (e.id === currentEdgeId || (e.startNodeId !== nodeId && e.endNodeId !== nodeId)) continue;
      const unit = edgeUnit(skeleton, e.id);
      if (!unit) continue;
      const sign = e.startNodeId === nodeId ? 1 : -1;
      const awayX = unit.ux * sign;
      const awayY = unit.uy * sign;

      // Side-aware: only a continuation face PROTRUDING past the current one
      // (toward the object) can collide — a recessed face never blocks.
      const facesContinue = objectNorm
        ? faceSizeToward(unit, objectNorm.x, objectNorm.y) - faceSizeToward(current, objectNorm.x, objectNorm.y) <= FACE_MATCH_TOL
        : Math.abs(faceSizeToward(unit, -dir.y, dir.x) - faceSizeToward(current, -dir.y, dir.x)) <= FACE_MATCH_TOL &&
          Math.abs(faceSizeToward(unit, dir.y, -dir.x) - faceSizeToward(current, dir.y, -dir.x)) <= FACE_MATCH_TOL;
      if (!continuation && awayX * dir.x + awayY * dir.y > COLINEAR_DOT && Math.abs(e.bulge ?? 0) < 1e-6 && facesContinue) {
        const a = skeleton.getNode(e.startNodeId);
        const b = skeleton.getNode(e.endNodeId);
        if (!a || !b) continue;
        continuation = {
          id: e.id,
          farNodeId: sign > 0 ? e.endNodeId : e.startNodeId,
          len: Math.hypot(b.position.x - a.position.x, b.position.y - a.position.y),
          away: { x: awayX, y: awayY },
        };
        continue;
      }

      // Transverse wall: its thickness reaches into the span — on the
      // object's side it blocks passage (a wall running off to the far side
      // can't collide with the object).
      if (objectNorm) {
        if (awayX * objectNorm.x + awayY * objectNorm.y < -0.01) continue;
        const perpDotInto = -unit.uy * -dir.x + unit.ux * -dir.y;
        pad = Math.max(pad, unit.left * perpDotInto, -unit.right * perpDotInto);
      }
    }
    if (!continuation || pad > 1e-4) return { ext, pad };
    ext += continuation.len;
    currentEdgeId = continuation.id;
    nodeId = continuation.farNodeId;
    dir = continuation.away;
  }
  return { ext, pad: 0 };
}

/** Side-agnostic usable along-line span of an edge, extended across colinear
 *  continuations (offsets may fall outside [0, len]) — draw/resize bounds. */
export function extendedRunSpan(skeleton: PointoramaWallSketch, edgeId: string): { lo: number; hi: number } {
  const geom = wallEdgeGeometry(skeleton, edgeId);
  if (!geom) return { lo: 0, hi: 0 };
  return {
    lo: -colinearSpanReach(skeleton, edgeId, 'start', null).ext,
    hi: geom.len + colinearSpanReach(skeleton, edgeId, 'end', null).ext,
  };
}

/** Wall material within this depth behind the face still counts as support
 *  (detected thicknesses vary; slight recesses shouldn't end the wall). */
const SUPPORT_DEPTH_TOL = 0.05;
/** Gap between support bodies still bridged as one continuous wall (m). */
const SUPPORT_MERGE_GAP = 0.05;
/** A body must protrude at least this far past the face to block sliding. */
const BLOCK_EPS = 0.02;

/**
 * Min/max along-coordinate of a wall-body polygon CLIPPED to the lateral
 * band [nLo, nHi]; null when the body doesn't reach the band. Clipping is
 * essential: a long wall only slightly off-perpendicular would otherwise
 * smear its along-extent by `length·sin(tilt)` and block far from where its
 * material actually reaches the band.
 */
function bodySpanInBand(ring: Array<{ s: number; n: number }>, nLo: number, nHi: number): { minS: number; maxS: number } | null {
  let poly = ring;
  for (const [sign, bound] of [
    [1, nLo],
    [-1, nHi],
  ] as const) {
    const next: Array<{ s: number; n: number }> = [];
    for (let i = 0; i < poly.length; i++) {
      const prev = poly[(i + poly.length - 1) % poly.length];
      const cur = poly[i];
      const prevIn = sign * (prev.n - bound) >= 0;
      const curIn = sign * (cur.n - bound) >= 0;
      if (curIn !== prevIn) {
        const t = (bound - prev.n) / (cur.n - prev.n);
        next.push({ s: prev.s + (cur.s - prev.s) * t, n: bound });
      }
      if (curIn) next.push(cur);
    }
    poly = next;
    if (poly.length === 0) return null;
  }
  let minS = Infinity;
  let maxS = -Infinity;
  for (const p of poly) {
    minS = Math.min(minS, p.s);
    maxS = Math.max(maxS, p.s);
  }
  return { minS, maxS };
}

/**
 * Usable slide interval for an object resting flush on `geom`'s face, in the
 * edge's along-line coordinate (may extend well beyond [0, len]). PURELY
 * GEOMETRIC — projects every wall BODY onto the face line, so it works
 * across T-junctions, centerline jogs and thickness steps where topological
 * edge-chaining fails:
 *  - SUPPORT: the object may only sit where wall material reaches the face
 *    line behind it (merged along-intervals of all such bodies).
 *  - BLOCKERS: bodies protruding past the face INTO the object's band bound
 *    the interval on either side of the cursor.
 * Returns the CENTER's clamp bounds (support/blockers minus `alongHalf`).
 * Null for curved edges (callers fall back to the edge's own span).
 */
export function wallSlideInterval(
  skeleton: PointoramaWallSketch,
  geom: WallEdgeGeometry,
  side: boolean,
  cursorS: number,
  flushHalf: number,
  alongHalf: number,
): { lo: number; hi: number } | null {
  if (Math.abs(geom.bulge) > 1e-6) return null;
  const dx = geom.b.x - geom.a.x;
  const dy = geom.b.y - geom.a.y;
  const len = Math.hypot(dx, dy);
  if (len < 1e-6) return null;
  const ux = dx / len;
  const uy = dy / len;
  const sideSign = side ? 1 : -1;
  const nx = -uy * sideSign;
  const ny = ux * sideSign;
  const face = side ? geom.leftSize : geom.rightSize;
  const bandOuter = face + 2 * flushHalf;

  const supports: Array<{ lo: number; hi: number }> = [];
  let loBlock = -Infinity;
  let hiBlock = Infinity;

  for (const e of skeleton.getEdges()) {
    const a = skeleton.getNode(e.startNodeId);
    const b = skeleton.getNode(e.endNodeId);
    if (!a || !b) continue;
    const edx = b.position.x - a.position.x;
    const edy = b.position.y - a.position.y;
    const elen = Math.hypot(edx, edy);
    if (elen < 1e-6) continue;
    // Body ring: centerline endpoints offset by the left/right sizes along
    // the edge's left normal (chord approximation for curved neighbours),
    // projected into this wall's (s, n) frame.
    const pnx = -edy / elen;
    const pny = edx / elen;
    const l = e.leftSize ?? 0.1;
    const r = e.rightSize ?? 0.1;
    const ring = (
      [
        [a.position.x + pnx * l, a.position.y + pny * l],
        [b.position.x + pnx * l, b.position.y + pny * l],
        [b.position.x - pnx * r, b.position.y - pny * r],
        [a.position.x - pnx * r, a.position.y - pny * r],
      ] as const
    ).map(([px, py]) => ({
      s: (px - geom.a.x) * ux + (py - geom.a.y) * uy,
      n: (px - geom.a.x) * nx + (py - geom.a.y) * ny,
    }));

    // Support: material reaching the shallow strip just behind the face.
    const support = bodySpanInBand(ring, face - SUPPORT_DEPTH_TOL, face);
    if (support) supports.push({ lo: support.minS, hi: support.maxS });

    // Blocker: material protruding past the face into the object's band.
    const block = bodySpanInBand(ring, face + BLOCK_EPS, bandOuter);
    if (block) {
      if (block.maxS <= cursorS) loBlock = Math.max(loBlock, block.maxS);
      else if (block.minS >= cursorS) hiBlock = Math.min(hiBlock, block.minS);
      else if (cursorS - block.minS < block.maxS - cursorS) hiBlock = Math.min(hiBlock, block.minS);
      else loBlock = Math.max(loBlock, block.maxS);
    }
  }

  // Merge the support intervals, then take the one the cursor is in (fall
  // back to the hit edge's own span if the cursor sits in a support gap).
  supports.sort((s1, s2) => s1.lo - s2.lo);
  const merged: Array<{ lo: number; hi: number }> = [];
  for (const span of supports) {
    const last = merged[merged.length - 1];
    if (last && span.lo <= last.hi + SUPPORT_MERGE_GAP) last.hi = Math.max(last.hi, span.hi);
    else merged.push({ ...span });
  }
  let supLo = 0;
  let supHi = len;
  for (const span of merged) {
    if (span.lo <= cursorS && cursorS <= span.hi) {
      supLo = span.lo;
      supHi = span.hi;
      break;
    }
  }

  return {
    lo: Math.max(supLo, loBlock) + alongHalf,
    hi: Math.min(supHi, hiBlock) - alongHalf,
  };
}

/** Reach (m) short of actual contact at which shape-touch snapping engages. */
const SHAPE_TOUCH_MARGIN = 0.02;
/** Margin (m) by which another wall's face must be closer to the cursor than
 *  the sticky wall's before the alignment switches — anti-jitter at
 *  junctions without making walls hard to leave deliberately. */
const STICKY_SWITCH_MARGIN = 0.2;

/**
 * The wall edge whose cursor-side face the object's FOOTPRINT touches.
 * Cursor-independent: a large object can already touch a wall while the
 * cursor (its center) is far outside any pixel snap reach. Among touching
 * walls the face nearest the CURSOR wins — NOT the deepest hypothetical
 * overlap, which would bias toward end-on walls with large flush extents.
 * A `stickyEdgeId` keeps winning while it stays touched unless another
 * wall's face is closer by {@link STICKY_SWITCH_MARGIN} — so working along a
 * wall doesn't jitter at junctions, yet deliberately approaching another
 * wall re-sticks. `extentsFor` supplies the object's half-extents toward /
 * along a candidate wall (orientation-dependent for free objects, fixed for
 * wall-attached ones). Null when the shape is clear of every wall.
 */
export function pickTouchedWallEdge(
  ctx: PlacementContext,
  extentsFor: (tangent: Pt2, side: boolean) => { flushHalf: number; alongHalf: number },
  stickyEdgeId: string | null,
): WallEdgeHit | null {
  const skeleton = ctx.wallSketch;
  if (!skeleton) return null;
  const floor = projectMouseToFloor(ctx.event, ctx.viewer.renderer.domElement, ctx.viewer.scene.getActiveCamera(), skeleton.getFloorHeight());
  if (!floor) return null;
  return pickTouchedWallEdgeAt(skeleton, floor, extentsFor, stickyEdgeId);
}

/** Point-based core of {@link pickTouchedWallEdge}: `floor` plays the
 *  cursor's role (the transform gizmo passes its candidate object center). */
export function pickTouchedWallEdgeAt(
  skeleton: PointoramaWallSketch,
  floor: Pt2,
  extentsFor: (tangent: Pt2, side: boolean) => { flushHalf: number; alongHalf: number },
  stickyEdgeId: string | null,
): WallEdgeHit | null {
  const floorZ = skeleton.getFloorHeight();

  let best: WallEdgeHit | null = null;
  let bestRank = Infinity;
  let sticky: WallEdgeHit | null = null;
  let stickyRank = Infinity;
  for (const edge of skeleton.getEdges()) {
    const geom = wallEdgeGeometry(skeleton, edge.id);
    if (!geom) continue;
    const foot = closestPointOnEdge({ x: floor.x, y: floor.y }, geom.a, geom.b, geom.bulge);
    const s = Math.max(0, Math.min(geom.len, foot.s));
    const { point, tangent } = pointAtArcLength(geom.a, geom.b, geom.bulge, s);
    const signedPerp = tangent.x * (floor.y - point.y) - tangent.y * (floor.x - point.x);
    const side = signedPerp > 0;
    const faceDistance = side ? geom.leftSize : geom.rightSize;
    const { flushHalf, alongHalf } = extentsFor(tangent, side);
    // Touch needs BOTH axes: the wall-facing side reaching the face AND the
    // footprint overlapping the edge's span — a cursor far past a short
    // edge's END is nowhere near its wall body even when it's barely off the
    // centerline's extension. closestPointOnEdge clamps to the segment, so
    // the overshoot past an end is the part of its true distance the
    // perpendicular component doesn't explain.
    const lateralGap = Math.abs(signedPerp) - faceDistance - flushHalf;
    const alongOvershoot = Math.sqrt(Math.max(0, foot.distance * foot.distance - signedPerp * signedPerp));
    if (lateralGap > SHAPE_TOUCH_MARGIN || alongOvershoot - alongHalf > SHAPE_TOUCH_MARGIN) continue;

    const hitHere: WallEdgeHit = { edgeId: geom.edgeId, offset: s, point: new THREE.Vector3(point.x, point.y, floorZ) };
    // Rank by the cursor's TRUE distance to the wall body, not just the
    // perpendicular component: past an edge's END the overshoot is real
    // distance too. Without it a T-junction stem whose line extension passes
    // under the cursor ranks ~0 and beats the wall the cursor is actually
    // beside — snapping the object through that wall onto the stem.
    const lateral = Math.abs(signedPerp) - faceDistance;
    const rank = alongOvershoot > 1e-6 ? Math.hypot(Math.max(lateral, 0), alongOvershoot) : lateral;
    if (edge.id === stickyEdgeId) {
      sticky = hitHere;
      stickyRank = rank;
    }
    if (rank < bestRank) {
      bestRank = rank;
      best = hitHere;
    }
  }
  if (sticky && bestRank >= stickyRank - STICKY_SWITCH_MARGIN) return sticky;
  return best;
}

/**
 * Cursor's UNCLAMPED along-line offset on a straight edge (null for curved
 * edges or when the cursor doesn't project). The wall pickers clamp their
 * hits to the edge's own span, so extended-span clamps need the raw
 * coordinate to let placements cross the edge's ends at T-junctions.
 */
export function unclampedCursorOffset(ctx: PlacementContext, geom: WallEdgeGeometry, floorZ: number): number | null {
  const floor = projectMouseToFloor(ctx.event, ctx.viewer.renderer.domElement, ctx.viewer.scene.getActiveCamera(), floorZ);
  if (!floor) return null;
  return unclampedOffsetAt(geom, floor);
}

/** Point-based core of {@link unclampedCursorOffset}. */
export function unclampedOffsetAt(geom: WallEdgeGeometry, point: Pt2): number | null {
  if (Math.abs(geom.bulge) > 1e-6) return null;
  const dx = geom.b.x - geom.a.x;
  const dy = geom.b.y - geom.a.y;
  const len = Math.hypot(dx, dy);
  if (len < 1e-6) return null;
  return ((point.x - geom.a.x) * dx + (point.y - geom.a.y) * dy) / len;
}

/**
 * Remap a wall hit that landed on a COLINEAR continuation edge into
 * `targetEdgeId`'s along-line coordinate (the offset may fall outside
 * [0, len]) — lets runs be drawn/dragged across T-junction nodes. Null when
 * the hit isn't on the target's line.
 */
export function remapHitToEdgeLine(skeleton: PointoramaWallSketch, targetEdgeId: string, hit: WallEdgeHit): WallEdgeHit | null {
  if (hit.edgeId === targetEdgeId) return hit;
  const geom = wallEdgeGeometry(skeleton, targetEdgeId);
  if (!geom || Math.abs(geom.bulge) > 1e-6) return null;
  const unit = edgeUnit(skeleton, targetEdgeId);
  if (!unit) return null;
  const px = hit.point.x - geom.a.x;
  const py = hit.point.y - geom.a.y;
  if (Math.abs(-unit.uy * px + unit.ux * py) > 0.02) return null;
  return { edgeId: targetEdgeId, offset: unit.ux * px + unit.uy * py, point: hit.point };
}

/**
 * Which face of the edge the cursor is on: true → the +90°-CCW side of the
 * local direction (matches `buildFrameFromWall`'s `side`). On curved edges
 * the side is judged against the LOCAL tangent at the cursor's foot point.
 */
export function wallSideFromCursor(ctx: PlacementContext, geom: WallEdgeGeometry, floorZ: number, fallback: { x: number; y: number }): boolean {
  const floor = projectMouseToFloor(ctx.event, ctx.viewer.renderer.domElement, ctx.viewer.scene.getActiveCamera(), floorZ) ?? new THREE.Vector3(fallback.x, fallback.y, floorZ);
  return wallSideAtPoint(geom, floor);
}

/** Point-based core of {@link wallSideFromCursor}. */
export function wallSideAtPoint(geom: WallEdgeGeometry, at: Pt2): boolean {
  const foot = closestPointOnEdge({ x: at.x, y: at.y }, geom.a, geom.b, geom.bulge);
  const { point, tangent } = pointAtArcLength(geom.a, geom.b, geom.bulge, foot.s);
  return tangent.x * (at.y - point.y) - tangent.y * (at.x - point.x) > 0;
}

function resolveOnEdge(ctx: PlacementContext, skeleton: PointoramaWallSketch, hit: WallEdgeHit, width: number, depth: number, inset: number): ResolvedPlacement | null {
  const geom = wallEdgeGeometry(skeleton, hit.edgeId);
  if (!geom) return null;
  const floorZ = skeleton.getFloorHeight();
  const side = wallSideFromCursor(ctx, geom, floorZ, hit.point);

  // The snap point is the object's center along the wall; clamp it
  // GEOMETRICALLY to where wall material backs the object (spans
  // T-junctions, jogs and thickness steps) and clear of protruding wall
  // bodies (real corners). The raw cursor coordinate is used — the hit's
  // offset is clamped to the edge's own span.
  const cursorS = unclampedCursorOffset(ctx, geom, floorZ) ?? hit.offset;
  const slide = wallSlideInterval(skeleton, geom, side, cursorS, inset + depth / 2, width / 2);
  const lo = slide ? slide.lo : width / 2;
  const hi = slide ? slide.hi : geom.len - width / 2;
  const center = lo <= hi ? Math.min(hi, Math.max(lo, cursorS)) : (lo + hi) / 2;
  const offset = center - width / 2;
  // Measured view rests the object on the MEASURED face LINE (position AND
  // rotation follow the face, which need not be parallel to the sketch edge);
  // ghost, placement and render frame resolve identically.
  const faceLine = skeleton.effectiveMeasuredFaceLineAt(geom.edgeId, side, center, width / 2);
  const z = floorZ + SKETCH_OBJECT_Z_LIFT;
  const frame = faceLine ? buildFrameFromWallFaceLine(pointAtArcLength(geom.a, geom.b, geom.bulge, center).point, faceLine, side, inset, width, depth, z) : buildFrameFromWall(geom.a, geom.b, offset, side, side ? geom.leftSize : geom.rightSize, inset, width, depth, z, geom.bulge);
  if (!frame) return null;
  return { frame, anchor: null, rotation: 0, wall: { edgeId: geom.edgeId, offset, side, inset } };
}

/**
 * Placement for a RUN drawn between two snap hits on the same wall edge
 * (Revit-style casework runs: LINE-anchored WALL types). The run's width is
 * the drawn span; the parametric glyph skins that length.
 */
export function resolveWallSpan(ctx: PlacementContext, type: RegisteredSketchObjectType, start: WallEdgeHit, end: WallEdgeHit): ResolvedPlacement | null {
  if (!ctx.wallSketch || start.edgeId !== end.edgeId) return null;
  const geom = wallEdgeGeometry(ctx.wallSketch, start.edgeId);
  if (!geom) return null;
  const lo = Math.min(start.offset, end.offset);
  const span = Math.abs(end.offset - start.offset);
  if (span < 1e-4) return null;

  const floorZ = ctx.wallSketch.getFloorHeight();
  const side = wallSideFromCursor(ctx, geom, floorZ, end.point);
  const inset = type.constraint.kind === 'WALL' ? (type.constraint.defaultInset ?? 0) : 0;
  const faceLine = ctx.wallSketch.effectiveMeasuredFaceLineAt(geom.edgeId, side, lo + span / 2, span / 2);
  const z = floorZ + SKETCH_OBJECT_Z_LIFT;
  const frame = faceLine
    ? buildFrameFromWallFaceLine(pointAtArcLength(geom.a, geom.b, geom.bulge, lo + span / 2).point, faceLine, side, inset, span, type.defaultSize.depth, z)
    : buildFrameFromWall(geom.a, geom.b, lo, side, side ? geom.leftSize : geom.rightSize, inset, span, type.defaultSize.depth, z, geom.bulge);
  if (!frame) return null;
  return { frame, anchor: null, rotation: 0, wall: { edgeId: geom.edgeId, offset: lo, side, inset } };
}

/** Sticky wall for WALL-constrained placement/drag (see pickTouchedWallEdge)
 *  — keeps a toilet from flip-flopping between the two walls of a corner. */
let wallStickyEdgeId: string | null = null;

/** Touch-based wall pick for a fixed-orientation WALL object (back to the
 *  wall), with the pixel snap as fallback for cursors hovering a wall the
 *  footprint doesn't reach yet. Maintains the WALL sticky state. */
function pickWallForObject(ctx: PlacementContext, width: number, depth: number, inset: number): WallEdgeHit | null {
  if (!ctx.wallSketch) return null;
  const hit =
    pickTouchedWallEdge(ctx, () => ({ flushHalf: inset + depth / 2, alongHalf: width / 2 }), wallStickyEdgeId) ??
    snapToWallEdge(ctx.event, ctx.wallSketch, ctx.hitTester, ctx.viewer.scene.getActiveCamera(), ctx.viewer.renderer.domElement, SKETCH_OBJECT_WALL_SNAP);
  wallStickyEdgeId = hit ? hit.edgeId : null;
  return hit;
}

export const wallConstraint: ConstraintStrategy = {
  kind: 'WALL',

  resolveHover(ctx: PlacementContext, type: RegisteredSketchObjectType, _rotation: number): ResolvedPlacement | null {
    if (!ctx.wallSketch) return null;
    const inset = type.constraint.kind === 'WALL' ? (type.constraint.defaultInset ?? 0) : 0;
    const hit = pickWallForObject(ctx, type.defaultSize.width, type.defaultSize.depth, inset);
    if (!hit) return null;
    return resolveOnEdge(ctx, ctx.wallSketch, hit, type.defaultSize.width, type.defaultSize.depth, inset);
  },

  resolveDrag(ctx: PlacementContext, object: PointoramaSketchObject, type: RegisteredSketchObjectType): ResolvedPlacement | null {
    if (!ctx.wallSketch) return null;
    const width = getSketchObjectWidthProperty(object.identifier).value ?? type.defaultSize.width;
    const depth = getSketchObjectDepthProperty(object.identifier).value ?? type.defaultSize.depth;
    const inset = getSketchObjectWallInsetProperty(object.identifier).value ?? 0;
    const hit = pickWallForObject(ctx, width, depth, inset);
    if (!hit) return null;
    return resolveOnEdge(ctx, ctx.wallSketch, hit, width, depth, inset);
  },

  writePlacement(objectId, values): void {
    if (!values.wall) return;
    getSketchObjectWallEdgeIdProperty(objectId).value = values.wall.edgeId;
    getSketchObjectWallOffsetProperty(objectId).value = values.wall.offset;
    getSketchObjectWallSideProperty(objectId).value = values.wall.side;
    getSketchObjectWallInsetProperty(objectId).value = values.wall.inset;
  },
};
